/**
 * Mail Owners Page - Eigentümer-Verwaltung für Mail Automation
 * Import, Liste, Suche, CRUD
 * Nur für UMitza sichtbar
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Upload, Search, Plus, Edit2, Trash2, Mail, RefreshCw, AlertCircle, CheckCircle2, XCircle, ChevronLeft, ChevronRight, X, Save } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MailOwnersPage = ({ user }) => {
  const [owners, setOwners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination & Search
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit/Create Modal
  const [editingOwner, setEditingOwner] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email_1: '',
    email_2: ''
  });
  const [saving, setSaving] = useState(false);

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser).token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const loadOwners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      if (search) {
        params.append('search', search);
      }
      
      const response = await axios.get(`${API}/mail/owners?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data.owners || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Eigentümer');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    setError(null);
    setSuccess(null);
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'add_only'); // Niemals überschreiben
      
      const response = await axios.post(`${API}/mail/owners/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      if (response.data.success) {
        setSuccess(`Import erfolgreich: ${response.data.imported} Eigentümer importiert, ${response.data.skipped} übersprungen`);
        await loadOwners();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingOwner(null);
    setFormData({
      first_name: '',
      last_name: '',
      email_1: '',
      email_2: ''
    });
  };

  const handleEdit = (owner) => {
    setEditingOwner(owner);
    setIsCreating(false);
    setFormData({
      first_name: owner.first_name || '',
      last_name: owner.last_name || '',
      email_1: owner.email_1 || '',
      email_2: owner.email_2 || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingOwner(null);
    setIsCreating(false);
    setFormData({
      first_name: '',
      last_name: '',
      email_1: '',
      email_2: ''
    });
  };

  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('Vor- und Nachname sind Pflichtfelder');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const token = getToken();
      const form = new FormData();
      form.append('first_name', formData.first_name.trim());
      form.append('last_name', formData.last_name.trim());
      if (formData.email_1) form.append('email_1', formData.email_1.trim());
      if (formData.email_2) form.append('email_2', formData.email_2.trim());
      
      if (isCreating) {
        await axios.post(`${API}/mail/owners`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Eigentümer erfolgreich erstellt');
      } else if (editingOwner) {
        await axios.put(`${API}/mail/owners/${editingOwner.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Eigentümer erfolgreich aktualisiert');
      }
      
      handleCancelEdit();
      await loadOwners();
    } catch (err) {
      setError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (owner) => {
    if (!window.confirm(`Möchten Sie "${owner.full_name}" wirklich löschen?`)) {
      return;
    }
    
    try {
      const token = getToken();
      await axios.delete(`${API}/mail/owners/${owner.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Eigentümer erfolgreich gelöscht');
      await loadOwners();
    } catch (err) {
      setError(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const totalPages = Math.ceil(total / limit);

  // Auto-hide messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-green-100 dark:bg-green-900/50 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Eigentümer-Verwaltung</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {total} Eigentümer in der Datenbank
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="import-excel-btn"
            >
              {importing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Excel importieren
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
              data-testid="create-owner-btn"
            >
              <Plus className="h-4 w-4" />
              Neu anlegen
            </button>
            <button
              onClick={loadOwners}
              disabled={loading}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Aktualisieren"
            >
              <RefreshCw className={`h-5 w-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-green-700 dark:text-green-300 text-sm">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Import Result Details */}
        {importResult && importResult.duplicates && importResult.duplicates.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              <span className="font-medium text-yellow-700 dark:text-yellow-300">
                {importResult.duplicates.length} Duplikate übersprungen
              </span>
            </div>
            <div className="max-h-32 overflow-y-auto text-sm text-yellow-600 dark:text-yellow-400">
              {importResult.duplicates.slice(0, 10).map((dup, idx) => (
                <div key={idx} className="py-1">
                  Zeile {dup.row}: &quot;{dup.name}&quot; existiert bereits als &quot;{dup.existing_name}&quot;
                </div>
              ))}
              {importResult.duplicates.length > 10 && (
                <div className="py-1 font-medium">... und {importResult.duplicates.length - 10} weitere</div>
              )}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Suche nach Name oder E-Mail..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              data-testid="owner-search-input"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingOwner) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              {isCreating ? (
                <>
                  <Plus className="h-5 w-5 text-green-500" />
                  Neuen Eigentümer anlegen
                </>
              ) : (
                <>
                  <Edit2 className="h-5 w-5 text-blue-500" />
                  Eigentümer bearbeiten: {editingOwner.full_name}
                </>
              )}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vorname *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Vorname"
                  data-testid="owner-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nachname *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Nachname"
                  data-testid="owner-last-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-Mail 1
                </label>
                <input
                  type="email"
                  value={formData.email_1}
                  onChange={(e) => setFormData({ ...formData, email_1: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="name@example.de"
                  data-testid="owner-email-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-Mail 2
                </label>
                <input
                  type="email"
                  value={formData.email_2}
                  onChange={(e) => setFormData({ ...formData, email_2: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="alternative@example.de"
                  data-testid="owner-email-2"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="owner-save-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Speichern
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Owners List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading && owners.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Lade Eigentümer...</p>
            </div>
          ) : owners.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="font-medium text-gray-900 dark:text-white mb-1">
                {search ? 'Keine Ergebnisse' : 'Keine Eigentümer vorhanden'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search ? 'Versuchen Sie einen anderen Suchbegriff' : 'Importieren Sie eine Excel-Datei oder legen Sie manuell Eigentümer an'}
              </p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <div className="col-span-3">Name</div>
                <div className="col-span-4">E-Mail 1</div>
                <div className="col-span-3">E-Mail 2</div>
                <div className="col-span-2 text-right">Aktionen</div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {owners.map((owner) => (
                  <div
                    key={owner.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {/* Name */}
                    <div className="md:col-span-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-medium text-sm">
                          {owner.first_name?.[0]}{owner.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {owner.full_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 md:hidden">
                            {owner.email_1 || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* E-Mail 1 */}
                    <div className="hidden md:flex md:col-span-4 items-center">
                      {owner.email_1 ? (
                        <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 text-gray-400" />
                          {owner.email_1}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                    
                    {/* E-Mail 2 */}
                    <div className="hidden md:flex md:col-span-3 items-center">
                      {owner.email_2 ? (
                        <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 text-gray-400" />
                          {owner.email_2}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="md:col-span-2 flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(owner)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Bearbeiten"
                        data-testid={`edit-owner-${owner.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(owner)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Löschen"
                        data-testid={`delete-owner-${owner.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Zeige {page * limit + 1} - {Math.min((page + 1) * limit, total)} von {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                  Seite {page + 1} von {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Import Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Hinweise zum Excel-Import
          </h3>
          <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
            <li>Unterstützte Formate: .xlsx, .xls</li>
            <li>Erwartete Spalten: &quot;Name&quot; (Pflicht), &quot;E-Mail 1&quot;, &quot;E-Mail 2&quot;</li>
            <li>Namen können als &quot;Nachname, Vorname&quot; oder &quot;Vorname Nachname&quot; formatiert sein</li>
            <li>Duplikate werden automatisch übersprungen (anhand E-Mail oder Name)</li>
            <li>Bestehende Einträge werden <strong>niemals überschrieben</strong></li>
          </ul>
        </div>
      </div>
    </div>
  );
};
